import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStockReport, type UseStockReportFilters } from '../../hooks/useStockReport';
import type { StockReportItem } from '../../types/reports';
import authService from '../../services/authService';

const PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 18mm 16mm 24mm 16mm;
  }

  body {
    background: #ffffff !important;
    color: #0f172a !important;
    font-family: 'Poppins', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif !important;
  }

  .print-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
    animation: fadeIn 0.4s ease-in;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .print-section-title {
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #0f172a;
    margin: 12px 0;
  }

  .print-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  .print-table thead th {
    background: #0f172a;
    color: #ffffff;
    padding: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .print-table tbody td {
    border: 1px solid #d9e2ec;
    padding: 10px 12px;
  }

  .print-table tbody tr:nth-of-type(odd) {
    background: #f8fafc;
  }

  @media print {
    .print-hide {
      display: none !important;
    }
  }
`;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatQuantity = (value: number, unit?: string) =>
  `${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ''}`;

const formatDateTime = (value?: string) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

interface ParsedFilters {
  startDate?: string;
  endDate?: string;
  searchTerm?: string;
  category?: string;
  includeInactive: boolean;
  includeZeroBalances: boolean;
  movementPageSize?: string;
  maxMovementPages?: string;
  cnpj?: string;
}

const parseFiltersFromSearch = (search: string): ParsedFilters => {
  const params = new URLSearchParams(search);
  return {
    startDate: params.get('startDate') || undefined,
    endDate: params.get('endDate') || undefined,
    searchTerm: params.get('searchTerm') || undefined,
    category: params.get('category') || undefined,
    includeInactive: params.get('includeInactive') === 'true',
    includeZeroBalances: params.get('includeZeroBalances') === 'true',
    movementPageSize: params.get('movementPageSize') || undefined,
    maxMovementPages: params.get('maxMovementPages') || undefined,
    cnpj: params.get('cnpj') || undefined,
  };
};

const buildQueryFilters = (filters: ParsedFilters, fallbackCnpj?: string): UseStockReportFilters => {
  const query: UseStockReportFilters = {};

  if (filters.startDate) {
    query.startDate = new Date(`${filters.startDate}T00:00:00`).toISOString();
  }

  if (filters.endDate) {
    query.endDate = new Date(`${filters.endDate}T23:59:59`).toISOString();
  }

  if (filters.searchTerm) {
    query.searchTerm = filters.searchTerm;
  }

  if (filters.category) {
    query.category = filters.category;
  }

  query.includeInactive = filters.includeInactive;
  query.includeZeroBalances = filters.includeZeroBalances;

  if (filters.movementPageSize) {
    const parsed = Number(filters.movementPageSize);
    if (!Number.isNaN(parsed)) {
      query.movementPageSize = parsed;
    }
  }

  if (filters.maxMovementPages) {
    const parsed = Number(filters.maxMovementPages);
    if (!Number.isNaN(parsed)) {
      query.maxMovementPages = parsed;
    }
  }

  const effectiveCnpj = filters.cnpj || fallbackCnpj;
  if (effectiveCnpj) {
    query.cnpj = effectiveCnpj.replace(/\D/g, '');
  }

  return query;
};

const StockReportPrintPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useMemo(() => authService.getUser(), []);

  const parsedFilters = useMemo(() => parseFiltersFromSearch(location.search), [location.search]);

  const queryFilters = useMemo(
    () => buildQueryFilters(parsedFilters, user?.cnpj),
    [parsedFilters, user?.cnpj],
  );

  const { data, isLoading, isError } = useStockReport(queryFilters, true);

  const summary = data?.summary;
  const items = useMemo<StockReportItem[]>(() => data?.items ?? [], [data]);
  const itemsWithRecipes = useMemo(() => items.filter((item) => item.recipeComponents.length > 0), [items]);
  const itemsWithLayers = useMemo(() => items.filter((item) => item.fifoLayers.length > 0), [items]);

  const totalizadores = useMemo(() => {
    if (!summary) {
      return {
        totalItems: items.length,
        totalQuantity: items.reduce((acc, item) => acc + (item.quantity ?? 0), 0),
        totalAvailable: items.reduce((acc, item) => acc + (item.availableQuantity ?? 0), 0),
        totalValue: items.reduce((acc, item) => acc + (item.totalValue ?? 0), 0),
      };
    }

    return {
      totalItems: summary.totalItems,
      totalQuantity: summary.totalQuantity,
      totalAvailable: summary.totalAvailable,
      totalValue: summary.totalValue,
    };
  }, [summary, items]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f4f6fb',
        py: 4,
        px: { xs: 2, md: 6 },
      }}
    >
      <style>{PRINT_STYLES}</style>

      <Box
        className="print-hide"
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Visualização para impressão do relatório de estoque
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revise as informações e clique em "Imprimir" para gerar o PDF. Você pode voltar e ajustar filtros sempre que precisar.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Voltar
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()} disabled={isLoading}>
            Imprimir
          </Button>
        </Box>
      </Box>

      {isError && (
        <Alert severity="error" className="print-hide" sx={{ mb: 3 }}>
          Não foi possível carregar os dados do relatório. Volte e ajuste os filtros antes de tentar novamente.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: '#ffffff',
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.12)',
          }}
        >
          <div className="print-wrapper">
            <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Relatório de estoque
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Emitido em {formatDateTime(new Date().toISOString())}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Período: {parsedFilters.startDate ?? '—'} – {parsedFilters.endDate ?? '—'}
                </Typography>
                {parsedFilters.searchTerm && (
                  <Typography variant="body2" color="text.secondary">
                    Filtro de item: {parsedFilters.searchTerm}
                  </Typography>
                )}
                {parsedFilters.category && (
                  <Typography variant="body2" color="text.secondary">
                    Categoria: {parsedFilters.category}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Incluir inativos: {parsedFilters.includeInactive ? 'Sim' : 'Não'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Incluir itens zerados: {parsedFilters.includeZeroBalances ? 'Sim' : 'Não'}
                </Typography>
              </Box>
              <Box textAlign="right">
                {user?.name && (
                  <Typography variant="body2" color="text.secondary">
                    Responsável: {user.name}
                  </Typography>
                )}
                {(parsedFilters.cnpj || user?.cnpj) && (
                  <Typography variant="body2" color="text.secondary">
                    CNPJ: {(parsedFilters.cnpj || user?.cnpj) ?? ''}
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider />

            <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Itens monitorados
                  </Typography>
                  <Typography variant="h5">{totalizadores.totalItems}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Quantidade total
                  </Typography>
                  <Typography variant="h5">{formatQuantity(totalizadores.totalQuantity)}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Valor total estimado
                  </Typography>
                  <Typography variant="h5">{formatCurrency(totalizadores.totalValue)}</Typography>
                </CardContent>
              </Card>
            </Box>

            <Divider />

            <div>
              <div className="print-section-title">Saldos consolidados</div>
              {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum item encontrado com os filtros selecionados.
                </Typography>
              ) : (
                <Table size="small" className="print-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Categoria</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                      <TableCell align="right">Reservado</TableCell>
                      <TableCell align="right">Disponível</TableCell>
                      <TableCell align="right">Custo médio</TableCell>
                      <TableCell align="right">Valor total</TableCell>
                      <TableCell align="right">Última movimentação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.itemId}>
                        <TableCell>
                          <Typography variant="subtitle2">{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {item.sku ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.category ?? '—'}</TableCell>
                        <TableCell align="right">{formatQuantity(item.quantity, item.unit)}</TableCell>
                        <TableCell align="right">{formatQuantity(item.reservedQuantity, item.unit)}</TableCell>
                        <TableCell align="right">{formatQuantity(item.availableQuantity, item.unit)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.averageCost ?? 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.totalValue ?? 0)}</TableCell>
                        <TableCell align="right">{formatDateTime(item.lastMovementAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <Divider />

            <div>
              <div className="print-section-title">Fichas técnicas associadas</div>
              {itemsWithRecipes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma ficha técnica registrada para os itens filtrados.
                </Typography>
              ) : (
                <List dense>
                  {itemsWithRecipes.map((item) => (
                    <ListItem key={item.itemId} alignItems="flex-start" sx={{ pb: 2 }}>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2">{item.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Estoque disponível: {formatQuantity(item.availableQuantity, item.unit)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box display="flex" flexDirection="column" gap={0.5} mt={1}>
                            {item.recipeComponents.map((component) => (
                              <Typography variant="caption" color="text.secondary" key={component.componentItemId}>
                                • {component.componentName ?? `Item ${component.componentItemId}`} —{' '}
                                {formatQuantity(component.quantity, component.unit)}
                              </Typography>
                            ))}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </div>

            <Divider />

            <div>
              <div className="print-section-title">Camadas de custo (FIFO)</div>
              {itemsWithLayers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma camada de custo registrada para os itens filtrados.
                </Typography>
              ) : (
                <Table size="small" className="print-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Lote</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                      <TableCell align="right">Custo unitário</TableCell>
                      <TableCell align="right">Custo total</TableCell>
                      <TableCell>Recebido em</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemsWithLayers.flatMap((item) =>
                      item.fifoLayers.map((layer, index) => (
                        <TableRow key={`${item.itemId}-${index}`}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{layer.lotId ?? '—'}</TableCell>
                          <TableCell align="right">{formatQuantity(layer.quantity, item.unit)}</TableCell>
                          <TableCell align="right">{formatCurrency(layer.unitCost)}</TableCell>
                          <TableCell align="right">{formatCurrency(layer.unitCost * layer.quantity)}</TableCell>
                          <TableCell>{formatDateTime(layer.receivedAt)}</TableCell>
                        </TableRow>
                      )),
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </Box>
      )}
    </Box>
  );
};

export default StockReportPrintPage;
